import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const AUTO_BUBBLE_COUNT = 20;
const AUTO_DROP_COUNT = 14;
const BURST_TTL_MS = 1300;
const SPLASH_TTL_MS = 950;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function createBubble(seed, overrides = {}) {
  const size = overrides.size || (6 + Math.round(Math.random() * 16));
  return {
    id: `bubble-${seed}`,
    group: overrides.group || "base",
    left: overrides.left || `${2 + Math.random() * 96}%`,
    size,
    delay: overrides.delay || `${-(Math.random() * 10).toFixed(2)}s`,
    duration: overrides.duration || `${8.5 + Math.random() * 9.5}s`,
    drift: overrides.drift || `${-20 + Math.random() * 40}px`,
    opacity: overrides.opacity || (0.13 + Math.random() * 0.18),
    pop: overrides.pop ?? Math.random() < 0.2,
    popAt: overrides.popAt || `${64 + Math.round(Math.random() * 24)}%`,
  };
}

function createDrop(seed) {
  return {
    id: `drop-${seed}`,
    left: `${2 + Math.random() * 96}%`,
    width: `${1 + Math.random() * 1.2}px`,
    height: `${26 + Math.random() * 46}px`,
    delay: `${-(Math.random() * 6).toFixed(2)}s`,
    duration: `${6 + Math.random() * 5.8}s`,
    opacity: 0.18 + Math.random() * 0.16,
  };
}

function createBurst(x, y, size, kind = "normal") {
  const safeSize = kind === "huge"
    ? clamp(size, 420, 1700)
    : kind === "drop"
      ? clamp(size, 8, 24)
      : kind === "bubble"
        ? clamp(size, 8, 20)
        : clamp(size, 12, 36);

  return {
    id: `${Date.now()}-${Math.random()}`,
    x,
    y,
    size: safeSize,
    kind,
  };
}

function createSplash(x, y, size) {
  return {
    id: `splash-${Date.now()}-${Math.random()}`,
    x,
    y,
    size: clamp(size, 8, 24),
  };
}

export default function BackgroundEffects() {
  const [baseBubbles, setBaseBubbles] = useState(() =>
    Array.from({ length: AUTO_BUBBLE_COUNT }, (_, index) => createBubble(`base-${index}`, { group: "base" })),
  );
  const [drops, setDrops] = useState(() =>
    Array.from({ length: AUTO_DROP_COUNT }, (_, index) => createDrop(`base-${index}`)),
  );

  const [extraBubbles, setExtraBubbles] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [splashes, setSplashes] = useState([]);
  const bubbleRefs = useRef(new Map());
  const dropRefs = useRef(new Map());

  const allBubbles = useMemo(() => [...baseBubbles, ...extraBubbles], [baseBubbles, extraBubbles]);

  const appendBurst = useCallback((burst) => {
    const ttl = burst.kind === "huge" ? BURST_TTL_MS * 2 : BURST_TTL_MS;
    setBursts((current) => [...current.slice(-24), burst]);
    window.setTimeout(() => {
      setBursts((current) => current.filter((item) => item.id !== burst.id));
    }, ttl);
  }, []);

  const appendSplash = useCallback((splash) => {
    setSplashes((current) => [...current.slice(-32), splash]);
    window.setTimeout(() => {
      setSplashes((current) => current.filter((item) => item.id !== splash.id));
    }, SPLASH_TTL_MS);
  }, []);

  const appendExtraBubbles = useCallback((createdBubbles, lifetimeMs = 24000) => {
    if (!createdBubbles?.length) return;
    const withGroup = createdBubbles.map((item) => ({ ...item, group: "extra" }));
    setExtraBubbles((current) => [...current.slice(-28), ...withGroup]);
    window.setTimeout(() => {
      const ids = new Set(withGroup.map((item) => item.id));
      setExtraBubbles((current) => current.filter((item) => !ids.has(item.id)));
    }, lifetimeMs);
  }, []);

  const popBubbleById = useCallback((bubbleId) => {
    if (!bubbleId) return;
    let removedBase = false;
    setBaseBubbles((current) => {
      const exists = current.some((item) => item.id === bubbleId);
      if (!exists) return current;
      removedBase = true;
      return current.filter((item) => item.id !== bubbleId);
    });
    setExtraBubbles((current) => current.filter((item) => item.id !== bubbleId));

    if (removedBase) {
      window.setTimeout(() => {
        setBaseBubbles((current) =>
          [...current, createBubble(`base-${Date.now()}-${Math.random()}`, { group: "base" })].slice(-AUTO_BUBBLE_COUNT),
        );
      }, 420 + Math.random() * 620);
    }
  }, []);

  const popDropById = useCallback((dropId) => {
    if (!dropId) return;
    let removed = false;
    setDrops((current) => {
      const exists = current.some((item) => item.id === dropId);
      if (!exists) return current;
      removed = true;
      return current.filter((item) => item.id !== dropId);
    });

    if (removed) {
      window.setTimeout(() => {
        setDrops((current) =>
          [...current, createDrop(`base-${Date.now()}-${Math.random()}`)].slice(-AUTO_DROP_COUNT),
        );
      }, 260 + Math.random() * 460);
    }
  }, []);

  useEffect(() => {
    const refillLoop = window.setInterval(() => {
      setBaseBubbles((current) => {
        if (current.length >= AUTO_BUBBLE_COUNT) return current.slice(-AUTO_BUBBLE_COUNT);
        const missing = AUTO_BUBBLE_COUNT - current.length;
        const created = Array.from({ length: missing }, (_, index) =>
          createBubble(`base-refill-${Date.now()}-${index}-${Math.random()}`, { group: "base" }),
        );
        return [...current, ...created].slice(-AUTO_BUBBLE_COUNT);
      });
      setDrops((current) => {
        if (current.length >= AUTO_DROP_COUNT) return current.slice(-AUTO_DROP_COUNT);
        const missing = AUTO_DROP_COUNT - current.length;
        const created = Array.from({ length: missing }, (_, index) =>
          createDrop(`base-refill-${Date.now()}-${index}-${Math.random()}`),
        );
        return [...current, ...created].slice(-AUTO_DROP_COUNT);
      });
    }, 3600);

    return () => window.clearInterval(refillLoop);
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      const hugeBurst = Math.random() < 0.01;
      const size = hugeBurst ? 780 + Math.random() * 700 : 22 + Math.random() * 30;
      appendBurst(createBurst(event.clientX, event.clientY, size, hugeBurst ? "huge" : "normal"));

      if (Math.random() < 0.015) {
        const nextBubble = createBubble(`extra-${Date.now()}-${Math.random()}`, { group: "extra" });
        appendExtraBubbles([nextBubble], 22000);
      }

      if (hugeBurst) {
        const chance = clamp(0.34 + size / 1800, 0.34, 0.92);
        window.setTimeout(() => {
          if (Math.random() > chance) return;
          const count = clamp(Math.round(size / 260), 3, 8);
          const screenWidth = Math.max(window.innerWidth || 0, 1);
          const radiusPx = size * 0.32;
          const created = Array.from({ length: count }, (_, index) => {
            const offset = (Math.random() - 0.5) * radiusPx;
            const leftPct = clamp(((event.clientX + offset) / screenWidth) * 100, 2, 98);
            return createBubble(`huge-${Date.now()}-${index}-${Math.random()}`, {
              group: "extra",
              left: `${leftPct}%`,
              size: 8 + Math.round(Math.random() * 10),
              duration: `${8 + Math.random() * 10}s`,
              opacity: 0.2 + Math.random() * 0.2,
              pop: Math.random() < 0.38,
            });
          });
          appendExtraBubbles(created, 26000);
        }, 2000);
      }
    };

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [appendBurst, appendExtraBubbles]);

  useEffect(() => {
    const bubbleBurstLoop = window.setInterval(() => {
      if (Math.random() > 0.07) return;
      const candidates = Array.from(bubbleRefs.current.entries()).filter(([, node]) => Boolean(node));
      if (!candidates.length) return;
      const [bubbleId, target] = candidates[Math.floor(Math.random() * candidates.length)];
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = rect.left + rect.width * (0.2 + Math.random() * 0.6);
      const y = rect.top + rect.height * (0.2 + Math.random() * 0.6);
      const size = 8 + Math.max(rect.width, rect.height) * (0.2 + Math.random() * 0.3);
      appendBurst(createBurst(x, y, size, "bubble"));
      popBubbleById(bubbleId);
    }, 2200);

    return () => window.clearInterval(bubbleBurstLoop);
  }, [appendBurst, popBubbleById, allBubbles.length]);

  useEffect(() => {
    const dropBurstLoop = window.setInterval(() => {
      const candidates = Array.from(dropRefs.current.entries()).filter(([, node]) => Boolean(node));
      if (!candidates.length) return;

      const [dropId, target] = candidates[Math.floor(Math.random() * candidates.length)];
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      if (Math.random() > 0.08) return;

      const x = rect.left + rect.width * (0.3 + Math.random() * 0.4);
      const y = rect.top + rect.height * Math.random();
      const size = 8 + rect.height * (0.14 + Math.random() * 0.26);

      appendBurst(createBurst(x, y, size, "drop"));
      appendSplash(createSplash(x, y, size * (0.8 + Math.random() * 0.22)));
      popDropById(dropId);
    }, 2200);

    return () => window.clearInterval(dropBurstLoop);
  }, [appendBurst, appendSplash, popDropById, drops.length]);

  return (
    <div className="fx-layer" aria-hidden="true">
      <div className="fx-aurora fx-aurora--top" />
      <div className="fx-aurora fx-aurora--bottom" />
      <div className="fx-grid" />

      <div className="fx-bubbles">
        {allBubbles.map((bubble) => (
          <span
            className={`fx-bubble ${bubble.pop ? "is-pop" : ""}`}
            key={bubble.id}
            ref={(node) => {
              if (node) bubbleRefs.current.set(bubble.id, node);
              else bubbleRefs.current.delete(bubble.id);
            }}
            style={{
              left: bubble.left,
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              animationName: bubble.pop ? "bubbleRisePop" : "bubbleRise",
              animationDelay: bubble.delay,
              animationDuration: bubble.duration,
              "--drift": bubble.drift,
              "--bubble-opacity": bubble.opacity,
              "--pop-at": bubble.popAt,
            }}
          />
        ))}
      </div>

      <div className="fx-drops">
        {drops.map((drop) => (
          <span
            className="fx-drop"
            key={drop.id}
            ref={(node) => {
              if (node) dropRefs.current.set(drop.id, node);
              else dropRefs.current.delete(drop.id);
            }}
            style={{
              left: drop.left,
              width: drop.width,
              height: drop.height,
              animationDelay: drop.delay,
              animationDuration: drop.duration,
              opacity: drop.opacity,
            }}
          />
        ))}
      </div>

      <div className="fx-bursts">
        {bursts.map((burst) => (
          <span
            className={`fx-burst ${burst.kind === "huge" ? "is-huge" : ""}`}
            key={burst.id}
            style={{
              left: `${burst.x}px`,
              top: `${burst.y}px`,
              width: `${burst.size}px`,
              height: `${burst.size}px`,
              marginLeft: `${-burst.size / 2}px`,
              marginTop: `${-burst.size / 2}px`,
            }}
          />
        ))}
      </div>

      <div className="fx-splashes">
        {splashes.map((splash) => (
          <span
            className="fx-splash"
            key={splash.id}
            style={{
              left: `${splash.x}px`,
              top: `${splash.y}px`,
              width: `${splash.size}px`,
              height: `${splash.size}px`,
              marginLeft: `${-splash.size / 2}px`,
              marginTop: `${-splash.size / 2}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
